import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Input, Button, List, Avatar, Badge, Space, Divider, Tabs } from "antd";
import { MessageOutlined, SendOutlined, CheckCircleOutlined, UserOutlined, CustomerServiceOutlined, FileTextOutlined, ReloadOutlined } from "@ant-design/icons";
import { getHistorialSolicitud, addMensaje, cerrarSolicitud, confirmarLecturaComentario, reloadAllSilencioso } from "../../../context/actions/solicitudesActions";
import { FilesList } from "./components/FilesList";
import { FileUpload } from "./components/FileUpload";
import dayjs from "dayjs";

const { TextArea } = Input;

export const ModalChat = ({ visible, onClose, solicitud }) => {
  const dispatch = useDispatch();
  const { 
    historialActual, 
    loading, 
    solicitudActiva, 
    archivosActuales 
  } = useSelector((state) => state.solicitudesReducer);
  const { user } = useSelector((state) => state.userReducer);
  
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [enviandoMensaje, setEnviandoMensaje] = useState(false);
  const [tabActivo, setTabActivo] = useState("mensajes");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (visible && solicitud) {
      dispatch(getHistorialSolicitud(solicitud.proveedorSolicitudID));
    }
  }, [visible, solicitud, dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [historialActual]);

  useEffect(() => {
    if (visible && historialActual.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [visible, historialActual.length]);

  useEffect(() => {
    if (visible && solicitud?.proveedorSolicitudID) {
      dispatch(confirmarLecturaComentario(solicitud.proveedorSolicitudID));
    }
  }, [visible, solicitud?.proveedorSolicitudID, dispatch]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.closest('.atencion-chat-container-moderno');
      if (container) {
        container.scrollTop = container.scrollHeight;
      } else {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleEnviarMensaje = async () => {
    if (!nuevoMensaje.trim()) return;

    setEnviandoMensaje(true);
    
    const mensajeData = {
      solicitudID: solicitud.proveedorSolicitudID,
      comentario: nuevoMensaje.trim()
    };

    await dispatch(addMensaje(mensajeData));
    setNuevoMensaje("");
    setEnviandoMensaje(false);
  };

  const handleCerrarSolicitud = () => {
    Modal.confirm({
      title: "¿Cerrar solicitud?",
      content: "Esta acción cerrará la solicitud definitivamente. ¿Deseas continuar?",
      okText: "Sí, cerrar",
      cancelText: "Cancelar",
      onOk: () => {
        dispatch(cerrarSolicitud(solicitud.proveedorSolicitudID));
        onClose();
      }
    });
  };

  const handleRecargarConversacion = () => {
    if (solicitud?.proveedorSolicitudID) {
      dispatch(reloadAllSilencioso(solicitud.proveedorSolicitudID));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnviarMensaje();
    }
  };

  const esSolicitudCerrada = solicitud?.estatus === "Cerrado";

  const esMensajePropio = (mensaje) => {
    return mensaje.usuarioNombre === user?.nombreCompleto || 
           mensaje.usuarioNombre === user?.nombre ||
           mensaje.usuarioID === user?.usuarioID;
  };

  const renderMensaje = (mensaje) => {
    const esPropio = esMensajePropio(mensaje);
    const esAdmin = mensaje.usuarioNombre?.toLowerCase().includes('admin') || 
                   mensaje.usuarioNombre?.toLowerCase().includes('soporte');
    
    return (
      <div className={`atencion-mensaje-wrapper-moderno ${esPropio ? 'es-mensaje-propio' : 'es-mensaje-externo'} ${esAdmin ? 'es-mensaje-admin' : ''}`}>
        <div className="atencion-burbuja-chat-moderna">
          <div className="atencion-mensaje-header-unico">
            <div className="atencion-usuario-info-unica">
              <Avatar 
                size="small" 
                icon={esAdmin ? <CustomerServiceOutlined /> : <UserOutlined />}
                style={{ 
                  backgroundColor: esPropio ? '#ff7b00' : esAdmin ? '#52c41a' : '#1890ff'
                }}
              />
              <span className="atencion-usuario-nombre-unico">{mensaje.usuarioNombre}</span>
            </div>
            <div className="atencion-mensaje-tiempo-unico">
              {dayjs(mensaje.fechaCreacion).format("HH:mm")}
            </div>
          </div>
          <div className="atencion-mensaje-contenido-unico">
            {mensaje.comentario}
          </div>
          <div className="atencion-mensaje-footer-unico">
            <div className="atencion-mensaje-fecha-unica">
              {dayjs(mensaje.fechaCreacion).format("DD/MM/YYYY")}
            </div>
            {/* {!mensaje.leido && (
              <Badge 
                status="warning" 
                text="No leído" 
                style={{ fontSize: '11px' }}
              />
            )} */}
          </div>
        </div>
      </div>
    );
  };

  const tabItems = [
    {
      key: 'mensajes',
      label: (
        <Space>
          <MessageOutlined />
          Mensajes
        </Space>
      ),
      children: (
        <div className="mensajes-tab-content">
          {/* lista de mensajes */}
          <div className="atencion-chat-container-moderno" style={{ height: "300px", overflowY: "auto" }}>
            {loading ? (
              <div className="mensaje-cargando">
                Cargando historial...
              </div>
            ) : historialActual.length > 0 ? (
              <div className="atencion-mensajes-lista-nueva">
                {historialActual
                  .sort((a, b) => new Date(a.fechaCreacion) - new Date(b.fechaCreacion))
                  .map((mensaje, index) => (
                    <div key={mensaje.id || index} className="atencion-mensaje-item-unico">
                      {renderMensaje(mensaje)}
                    </div>
                  ))}
              </div>
            ) : (
              <div className="sin-mensajes">
                <MessageOutlined style={{ fontSize: '24px', color: '#ccc', marginBottom: '8px' }} />
                <p>No hay mensajes en esta solicitud</p>
                <p style={{ fontSize: '12px', color: '#999' }}>Escribe el primer mensaje para comenzar la conversación</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* input para nuevo mensaje (solo si no está cerrada) */}
          {!esSolicitudCerrada && (
            <>
              <Divider />
              <div className="chat-input-container">
                <div className="chat-input-wrapper">
                  <FileUpload solicitudId={solicitud?.proveedorSolicitudID} compact />
                  <div className="text-input-container">
                    <Input
                      placeholder="Escribe tu mensaje..."
                      value={nuevoMensaje}
                      onChange={(e) => setNuevoMensaje(e.target.value)}
                      onPressEnter={handleEnviarMensaje}
                      disabled={enviandoMensaje}
                      className="custom-message-input"
                    />
                  </div>
                  <div 
                    className={`custom-send-btn ${!nuevoMensaje.trim() ? 'disabled' : ''} ${enviandoMensaje ? 'loading' : ''}`}
                    onClick={!enviandoMensaje && nuevoMensaje.trim() ? handleEnviarMensaje : undefined}
                    title={enviandoMensaje ? "Enviando..." : !nuevoMensaje.trim() ? "Escribe un mensaje" : "Enviar mensaje"}
                  >
                    <SendOutlined />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )
    },
    {
      key: 'archivos',
      label: (
        <Space>
          <FileTextOutlined />
          Archivos
          {archivosActuales.length > 0 && (
            <Badge count={archivosActuales.length} size="small" />
          )}
        </Space>
      ),
      children: (
        <div className="archivos-tab-content" style={{ height: "380px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <FilesList solicitud={solicitud} />
        </div>
      )
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <MessageOutlined />
          <span>Solicitud #{solicitud?.proveedorSolicitudID}</span>
          <Badge 
            status={esSolicitudCerrada ? "default" : "processing"} 
            text={solicitud?.estatus || "Abierto"}
            className={solicitud?.estatus === "Abierto" ? "badge-abierto" : ""}
          />
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
      className="modal-chat"
      style={{ top: 20 }}
    >
      {solicitud && (
        <>
          {/* header con información de la solicitud */}
          <div className="chat-header">
            <div className="solicitud-info">
              <p><strong>Proveedor:</strong> {user?.nombreCompleto || user?.nombre}</p>
              <p><strong>Usuario:</strong> {solicitud.proveedorUsuarioNombre}</p>
              <p><strong>Descripción inicial:</strong> {solicitud.comentario}</p>
              <p><strong>Fecha:</strong> {dayjs(solicitud.fechaCreacion).format("DD/MM/YYYY HH:mm")}</p>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {!esSolicitudCerrada && (
                <Button 
                  icon={<CheckCircleOutlined />}
                  onClick={handleCerrarSolicitud}
                  size="default"
                  style={{ 
                    borderColor: '#52c41a',
                    color: '#52c41a',
                    backgroundColor: '#fff',
                    minWidth: '140px',
                    borderRadius: '6px'
                  }}
                >
                  Cerrar Solicitud
                </Button>
              )}
              <Button 
                type="default"
                icon={<ReloadOutlined />}
                onClick={handleRecargarConversacion}
                size="default"
                style={{ 
                  borderColor: '#1890ff',
                  color: '#1890ff',
                  minWidth: '140px',
                  borderRadius: '6px'
                }}
              >
                Recargar
              </Button>
            </div>
          </div>

          {/* <Divider /> */}
          <div style={{ marginTop: '16px' }}></div>

          {/* tabs para mensajes y archivos */}
          <Tabs
            activeKey={tabActivo}
            onChange={setTabActivo}
            items={tabItems}
            className="chat-tabs"
          />
        </>
      )}
      
      <style jsx>{`
        .chat-input-container {
          padding: 0;
        }
        
        .chat-input-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #fafafa;
          border-radius: 12px;
          border: 1px solid #e8e8e8;
          transition: all 0.2s ease;
        }
        
        .chat-input-wrapper:focus-within {
          border-color: #1890ff;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
          background: #fff;
        }
        
        .text-input-container {
          flex: 1;
        }
        
        .custom-message-input {
          border: none;
          box-shadow: none;
          background: transparent;
          padding: 8px 0;
          font-size: 14px;
        }
        
        .custom-message-input:focus {
          border: none;
          box-shadow: none;
          background: transparent;
        }
        
        .custom-message-input::placeholder {
          color: #bbb;
        }
        
        .custom-send-btn {
          width: 36px;
          height: 36px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 16px;
          box-shadow: 0 2px 4px rgba(24, 144, 255, 0.2);
        }
        
        .custom-send-btn:hover {
          background: linear-gradient(135deg, #096dd9 0%, #1890ff 100%);
          box-shadow: 0 4px 8px rgba(24, 144, 255, 0.3);
          transform: translateY(-1px);
        }
        
        .custom-send-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(24, 144, 255, 0.2);
        }
        
        .custom-send-btn.disabled {
          background: #f5f5f5;
          color: #ccc;
          cursor: not-allowed;
          box-shadow: none;
        }
        
        .custom-send-btn.disabled:hover {
          background: #f5f5f5;
          transform: none;
          box-shadow: none;
        }
        
        .custom-send-btn.loading {
          animation: sendPulse 1.5s infinite;
        }
        
        @keyframes sendPulse {
          0%, 100% { 
            opacity: 1; 
            transform: scale(1);
          }
          50% { 
            opacity: 0.8; 
            transform: scale(0.95);
          }
        }
      `}</style>
    </Modal>
  );
};